import { editModeState, setEditMode } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { pointSelectors } from '@trenches/redux';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { useAppDispatch } from '@core/redux/hooks';
import { useSelector } from 'react-redux';

window.isPointDebugModeActive = false;

/**
 * This function is for debugging purposes only.
 * It allows to focus on a point by entering its UUID in the search field.
 */
export const useSelectAndFocusPointsById = () => {
  const dispatch = useAppDispatch();
  const { mode } = useSnapshot(editModeState);
  const allPoints = useSelector(pointSelectors.selectAll);
  const selectedPointId = useSelector(viewer3dSelectors.selectedPointId);

  const isPointInStore = (pointId: string) => !!allPoints.find(({ id }) => id === pointId);

  useEffect(() => {
    if (window.isPointDebugModeActive && selectedPointId) {
      console.log(
        'Enter the next line into the address search field to select this point:',
        selectedPointId,
      );
    }
  }, [selectedPointId]);

  // Keep stable refs to avoid stale closures in globally attached window function
  const depsRef = useRef({ mode, dispatch, isPointInStore });
  useEffect(() => {
    depsRef.current = { mode, dispatch, isPointInStore };
  }, [mode, dispatch, allPoints]); // update when any of these change

  useEffect(() => {
    window.selectPointByUUID = (pointId: string) => {
      if (!window.isPointDebugModeActive) {
        return "Please set 'window.isPointDebugModeActive = true' to use this function.";
      }

      const {
        mode: currentMode,
        dispatch: currentDispatch,
        isPointInStore: currentIsPointInStore,
      } = depsRef.current;

      if (currentMode !== EditMode.Trench && currentMode !== EditMode.Element) {
        setEditMode(EditMode.Element);
      }

      if (!pointId.match(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/)) {
        return 'No valid uuid given for point! UUIDs have the format [0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}.';
      }

      if (!currentIsPointInStore(pointId)) {
        return `The entered UUID ${pointId} is no UUID of a point in the current project or points haven't been fully loaded yet.`;
      }

      currentDispatch(viewer3dActions.setSelectedPointId(pointId));
      // press "f" to focus on the selected point
      window.dispatchEvent(new window.KeyboardEvent('keydown', { key: 'f' }));
      return `Selected the Point '${pointId}'. Press "f" to focus on it if necessary.`;
    };

    return () => {
      Reflect.deleteProperty(window, 'selectPointByUUID');
    };
  }, []);
};
